import React from 'react';
import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Link, NavLink, useLocation } from "react-router-dom";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useMaterialTailwindController, setOpenSidenav } from "@/context";
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/24/solid";
import useHasPermission from "../../components/useHasPermission";
import { UserProfileMenu } from './UserProfileMenu';
import { getUserProfile } from '@/store/slices/userSlice';
import { useDispatch, useSelector } from "react-redux";

export function Sidenav({ brandImg, brandName, routes }) {
  const _dispatch = useDispatch();
  const [controller, dispatch] = useMaterialTailwindController();
  const { openSidenav } = controller;
  const location = useLocation();
  const [openMenus, setOpenMenus] = useState({});
  const [isCollapsed, setIsCollapsed] = useState(
    () => localStorage.getItem("sidenavCollapsed") === "1"
  );
  const user = useSelector((state) => state.user.user);

  const toggleCollapse = () => {
    const next = !isCollapsed;
    setIsCollapsed(next);
    localStorage.setItem("sidenavCollapsed", next ? "1" : "0");
    window.dispatchEvent(new CustomEvent("sidenavToggle", { detail: { collapsed: next } }));
  };

  useEffect(() => {
    const openMenu = routes.find(({ pages }) =>
      pages.some((page) => `/${page.layout}${page.path}` === location.pathname)
    )?.title;
    if (openMenu) {
      setOpenMenus({ [openMenu]: true });
    }
  }, [location.pathname, routes]);

  useEffect(() => {
    _dispatch(getUserProfile());
  }, []);

  const toggleMenu = (title) => {
    if (isCollapsed) return;
    setOpenMenus((prev) => ({
      [title]: !prev[title],
    }));
  };

  const baseLinkStyle = "flex items-center w-full h-9 px-4 rounded-lg text-left text-sm leading-normal transition-colors duration-200";
  const inactiveStyle = "text-[#736C64] font-normal hover:bg-gray-200/60";
  const activeStyle = "bg-[#EBE9E6] text-gray-900 font-semibold";

  return (
    <aside
      className={`fixed top-0 left-0 h-screen z-50 transform transition-all duration-300 border-r border-gray-200 flex flex-col bg-[#F7F5F2] lg:translate-x-0
        ${openSidenav ? "translate-x-0" : "-translate-x-full"}
        ${isCollapsed ? "w-[68px]" : "w-[286px]"}
      `}
    >
      {/* ## Logo Section ## */}
      <div className="relative flex items-center justify-between p-4 border-b border-gray-200/60 min-h-[76px]">
        {!isCollapsed && (
          <Link to="/" className="flex items-center gap-4">
            <img src={brandImg} alt={brandName} className="h-12 w-auto" />
          </Link>
        )}
        {isCollapsed && (
          <Link to="/" className="flex items-center justify-center w-full">
            <img src={brandImg} alt={brandName} className="h-8 w-8 object-contain" />
          </Link>
        )}

        {/* Mobile close button */}
        <button
          className="absolute right-0 top-1/2 -translate-y-1/2 grid rounded-none lg:hidden m-2 p-2"
          onClick={() => setOpenSidenav(dispatch, false)}
        >
          <XMarkIcon strokeWidth={3} className="h-4 w-4 text-gray-600" />
        </button>

        {/* Desktop collapse toggle */}
        <button
          className="hidden lg:flex items-center justify-center h-6 w-6 rounded-full bg-black border border-black shadow-sm text-white hover:bg-gray-800 absolute -right-3 top-1/2 -translate-y-1/2 z-10"
          onClick={toggleCollapse}
        >
          <ChevronRightIcon
            className={`h-3 w-3 text-white transition-transform duration-300 ${isCollapsed ? "rotate-0" : "rotate-180"}`}
          />
        </button>
      </div>

      {/* ## Navigation Links ## */}
      <div className="flex-grow px-2 py-4 space-y-1 overflow-y-auto overflow-x-hidden">
        {routes.map(({ layout, title, unique_key, image, pages }, index) => {
          if (title === '') return null;
          const hasPermission = useHasPermission;
          const validPages = (pages || []).filter(
            (p) => p?.name && p?.path && hasPermission(p.permission)
          );
          if (validPages.length === 0) return null;
          const isDropdown = (title) ? true : false;
          const firstPage = validPages[0];
          const key = title || firstPage?.name || index;
          const displayTitle = title || firstPage?.name;

          if (!isDropdown) {
            const { name, path } = firstPage;
            return (
              <div key={key}>
                <NavLink to={`/${layout}${path}`} end={path === '/'}>
                  {({ isActive }) => (
                    <button
                      title={isCollapsed ? name : undefined}
                      className={`${baseLinkStyle} gap-4 ${isActive ? activeStyle : inactiveStyle} ${isCollapsed ? "justify-center px-2" : ""}`}
                    >
                      {image &&
                        <img
                          src={`/img/icons/${image}.png`}
                          alt={name}
                          className="h-4 w-4 object-contain flex-shrink-0 max-w-[unset]"
                        />
                      }
                      {!isCollapsed && (
                        <span>
                          {name}{' '}
                          {['vendor', 'customer', 'product', 'internal_user', 'rfq', 'order', 'sub_admin'].includes(unique_key) &&
                            user?.sidebar_counts?.[unique_key] ? (
                            `(${user.sidebar_counts[unique_key]})`
                          ) : (
                            ''
                          )}
                        </span>
                      )}
                    </button>
                  )}
                </NavLink>
              </div>
            );
          }

          const isMenuOpen = !!openMenus[title];
          const isAnyChildActive = pages.some(p => location.pathname.includes(p.path));

          return (
            <div key={key}>
              <button
                onClick={() => toggleMenu(title)}
                title={isCollapsed ? displayTitle : undefined}
                className={`${baseLinkStyle} ${isCollapsed ? "justify-center px-2" : "justify-between"} ${isMenuOpen || isAnyChildActive ? 'text-gray-900 font-semibold' : inactiveStyle}`}
              >
                <div className={`flex items-center gap-4 ${isCollapsed ? "justify-center" : ""}`}>
                  {image &&
                    <img
                      src={`/img/icons/${image}.png`}
                      alt={title}
                      className="h-4 w-4 object-contain flex-shrink-0 max-w-[unset]"
                    />
                  }
                  {!isCollapsed && (
                    <span>
                      {displayTitle}{' '}
                      {['vendor', 'customer', 'product', 'internal_user', 'rfq', 'order', 'sub_admin'].includes(unique_key) &&
                        user?.sidebar_counts?.[unique_key] ? (
                        `(${user.sidebar_counts[unique_key]})`
                      ) : (
                        ''
                      )}
                    </span>
                  )}
                </div>
                {!isCollapsed && (
                  <ChevronDownIcon
                    className={`h-4 w-4 transition-transform duration-300 ${isMenuOpen ? "rotate-180" : ""}`}
                  />
                )}
              </button>

              {/* Sub-menu — hidden when collapsed */}
              {!isCollapsed && (
                <div className={`grid transition-all duration-300 ease-in-out ${isMenuOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
                  <div className="overflow-hidden">
                    <div className="pl-6 pt-2 flex flex-col space-y-2">
                      {validPages.map(({ icon: subIcon, name, path }) => (
                        <NavLink to={`/${layout}${path}`} key={name} end={false}>
                          {({ isActive }) => (
                            <button className={`${baseLinkStyle} gap-3 ${isActive ? activeStyle : inactiveStyle}`}>
                              {subIcon && React.cloneElement(subIcon, { className: "h-4 w-4" })}
                              <span>{name}</span>
                            </button>
                          )}
                        </NavLink>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ## User Profile Section ## */}
      <UserProfileMenu isCollapsed={isCollapsed} />
    </aside>
  );
}

Sidenav.defaultProps = {
  brandImg: "/img/logos/cs-logo.svg",
  brandName: "corporateSupplier",
};

Sidenav.propTypes = {
  brandImg: PropTypes.string,
  brandName: PropTypes.string,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

Sidenav.displayName = "/src/widgets/layout/sidenav.jsx";

export default Sidenav;